/**
 * Clickable stat-block strip. Each block toggles a filter on/off in one
 * click. The active block draws a colored border so admin can see at a
 * glance which slice is in view.
 *
 * Replaces the previous read-only "InstallStat" boxes on the wheels +
 * tires pages — same visuals, now interactive.
 */

import { Box, BlockStack, Text } from '@shopify/polaris';
import type { ReactNode } from 'react';

export type StatTone = 'success' | 'info' | 'attention' | 'warning' | 'critical' | 'new' | 'neutral';

export interface StatBlock {
  /** Stable id for active comparison. */
  id: string;
  label: string;
  count: number;
  tone?: StatTone;
  /** Click handler — typically toggles a filter in URL state. */
  onClick: () => void;
  /** When true, the block is rendered in its "active" state. */
  active: boolean;
  /** Optional small caption under the count. */
  caption?: string;
}

const TONE_BG: Record<StatTone, string> = {
  success:   '#E0F5DF',
  info:      '#E3F1FA',
  attention: '#FFF1B8',
  warning:   '#FFE5B5',
  critical:  '#FED3D1',
  new:       '#E4E5E7',
  neutral:   '#F6F6F7',
};

const TONE_BORDER: Record<StatTone, string> = {
  success:   '#1F8A38',
  info:      '#1A7CB8',
  attention: '#B98900',
  warning:   '#B86E00',
  critical:  '#D72C0D',
  new:       '#5C5F62',
  neutral:   '#5C5F62',
};

export function StatBadgeRow({ blocks }: { blocks: StatBlock[] }): ReactNode {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${blocks.length}, minmax(0, 1fr))`,
        gap: 12,
      }}
    >
      {blocks.map((b) => {
        const tone = b.tone ?? 'neutral';
        return (
          <button
            key={b.id}
            type="button"
            onClick={b.onClick}
            aria-pressed={b.active}
            style={{
              cursor: 'pointer',
              textAlign: 'left',
              padding: '12px 16px',
              borderRadius: 8,
              background: TONE_BG[tone],
              border: `2px solid ${b.active ? TONE_BORDER[tone] : 'transparent'}`,
              transition: 'border-color 120ms',
            }}
          >
            <Box>
              <BlockStack gap="050">
                <Text as="p" variant="bodySm" tone="subdued">{b.label}</Text>
                <Text as="p" variant="headingLg">{b.count.toLocaleString()}</Text>
                {b.caption && (
                  <Text as="p" variant="bodySm" tone="subdued">{b.caption}</Text>
                )}
              </BlockStack>
            </Box>
          </button>
        );
      })}
    </div>
  );
}
